import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [password, setPassword] = useState(() => localStorage.getItem("wc_admin_pw") || "");
  const [loggedIn, setLoggedIn] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);

  const authHeaders = () => ({ "X-Admin-Password": password });

  const verify = async (pw) => {
    try {
      await axios.post(`${API}/admin/verify`, { password: pw });
      localStorage.setItem("wc_admin_pw", pw);
      setLoggedIn(true);
      loadAll(pw);
    } catch {
      toast.error("Λάθος κωδικός / Wrong password");
    }
  };

  const loadAll = async (pw = password) => {
    const h = { "X-Admin-Password": pw };
    try {
      const [b, c, bl, sc, g, rv] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: h }),
        axios.get(`${API}/contact`, { headers: h }),
        axios.get(`${API}/blog?all=true`),
        axios.get(`${API}/schedule`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/reviews?all=true`),
      ]);
      setBookings(b.data);
      setContacts(c.data);
      setPosts(bl.data);
      setSchedule(sc.data);
      setGallery(g.data);
      setReviews(rv.data);
    } catch (e) {
      console.error("Admin data load failed:", e);
    }
  };

  useEffect(() => {
    if (password) verify(password);
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem("wc_admin_pw");
    setLoggedIn(false);
    setPassword("");
  };

  const del = async (path, id, refresh) => {
    if (!window.confirm("Delete?")) return;
    try {
      await axios.delete(`${API}/${path}/${id}`, { headers: authHeaders() });
      toast.success("Deleted");
      refresh();
    } catch { toast.error("Failed"); }
  };

  // ---------- Blog form ----------
  const emptyPost = { slug: "", title_el: "", title_en: "", excerpt_el: "", excerpt_en: "", content_el: "", content_en: "", cover_image: "", published: true };
  const [postForm, setPostForm] = useState(emptyPost);
  const createPost = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/blog`, postForm, { headers: authHeaders() });
      toast.success("Post created");
      setPostForm(emptyPost);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // ---------- Schedule form ----------
  const emptySch = { day_el: "", day_en: "", time: "", title_el: "", title_en: "", description_el: "", description_en: "", order: 0 };
  const [schForm, setSchForm] = useState(emptySch);
  const createSch = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/schedule`, schForm, { headers: authHeaders() });
      toast.success("Added");
      setSchForm(emptySch);
      loadAll();
    } catch { toast.error("Failed"); }
  };

  // ---------- Gallery form ----------
  const emptyGal = { type: "image", url: "", caption_el: "", caption_en: "", order: 0 };
  const [galForm, setGalForm] = useState(emptyGal);
  const createGal = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/gallery`, galForm, { headers: authHeaders() });
      toast.success("Added");
      setGalForm(emptyGal);
      loadAll();
    } catch { toast.error("Failed"); }
  };

  // ---------- Review form ----------
  const emptyRev = { name: "", rating: 5, class_type: "", text_el: "", text_en: "", published: true, order: 0 };
  const [revForm, setRevForm] = useState(emptyRev);
  const createRev = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reviews`, revForm, { headers: authHeaders() });
      toast.success("Review added");
      setRevForm(emptyRev);
      loadAll();
    } catch { toast.error("Failed"); }
  };

  if (!loggedIn) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-24">
        <form
          onSubmit={(e) => { e.preventDefault(); verify(password); }}
          className="card max-w-md w-full mx-4"
          data-testid="admin-login-form"
        >
          <span className="font-cn text-gold text-3xl block mb-4">管理</span>
          <h1 className="h-display text-3xl mb-6">Admin</h1>
          <input
            type="password"
            data-testid="admin-password"
            className="field mb-4"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button data-testid="admin-login-btn" type="submit" className="btn-primary w-full">Login</button>
        </form>
      </div>
    );
  }

  const tabs = [
    { key: "bookings", label: `Bookings (${bookings.length})` },
    { key: "contact", label: `Messages (${contacts.length})` },
    { key: "blog", label: "Blog" },
    { key: "schedule", label: "Schedule" },
    { key: "gallery", label: "Gallery" },
    { key: "reviews", label: `Reviews (${reviews.length})` },
  ];

  return (
    <div className="py-16">
      <div className="container-wc">
        <div className="flex items-center justify-between mb-10">
          <h1 className="h-display text-4xl">Admin Panel</h1>
          <button onClick={logout} data-testid="admin-logout" className="btn-outline"><LogOut size={14} /> Logout</button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-goldSoft">
          {tabs.map((tt) => (
            <button
              key={tt.key}
              data-testid={`tab-${tt.key}`}
              onClick={() => setTab(tt.key)}
              className={`px-4 py-3 text-sm uppercase tracking-widest ${tab === tt.key ? "text-gold border-b-2 border-gold" : "text-dim"}`}
            >
              {tt.label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="space-y-3">
            {bookings.length === 0 && <p className="text-dim">No bookings yet.</p>}
            {bookings.map((b) => (
              <div key={b.id} className="card flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-serif text-lg text-gold">{b.name}</span>
                    <span className="text-xs text-dim">{new Date(b.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-dim mt-1">{b.email} · {b.phone} · {b.city || "-"} / {b.area || "-"}</div>
                  <div className="text-sm mt-1"><span className="text-gold">{b.class_type}</span></div>
                  {b.message && <p className="text-sm text-dim mt-2 italic">&ldquo;{b.message}&rdquo;</p>}
                </div>
                <button onClick={() => del("bookings", b.id, loadAll)} className="text-primary hover:text-white"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "contact" && (
          <div className="space-y-3">
            {contacts.length === 0 && <p className="text-dim">No messages.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="card flex justify-between gap-4">
                <div className="flex-1">
                  <div className="font-serif text-lg text-gold">{c.name}</div>
                  <div className="text-sm text-dim">{c.email} · {c.phone}</div>
                  <p className="text-sm mt-2">{c.message}</p>
                </div>
                <button onClick={() => del("contact", c.id, loadAll)} className="text-primary"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "blog" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <form onSubmit={createPost} className="card space-y-3">
              <h3 className="font-serif text-xl">New Post</h3>
              <input required className="field" placeholder="slug (url-friendly)" value={postForm.slug} onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })} />
              <input required className="field" placeholder="Title EL" value={postForm.title_el} onChange={(e) => setPostForm({ ...postForm, title_el: e.target.value })} />
              <input required className="field" placeholder="Title EN" value={postForm.title_en} onChange={(e) => setPostForm({ ...postForm, title_en: e.target.value })} />
              <input className="field" placeholder="Cover image URL" value={postForm.cover_image} onChange={(e) => setPostForm({ ...postForm, cover_image: e.target.value })} />
              <textarea required className="field" placeholder="Excerpt EL" rows={2} value={postForm.excerpt_el} onChange={(e) => setPostForm({ ...postForm, excerpt_el: e.target.value })} />
              <textarea required className="field" placeholder="Excerpt EN" rows={2} value={postForm.excerpt_en} onChange={(e) => setPostForm({ ...postForm, excerpt_en: e.target.value })} />
              <textarea required className="field" placeholder="Content EL" rows={5} value={postForm.content_el} onChange={(e) => setPostForm({ ...postForm, content_el: e.target.value })} />
              <textarea required className="field" placeholder="Content EN" rows={5} value={postForm.content_en} onChange={(e) => setPostForm({ ...postForm, content_en: e.target.value })} />
              <button type="submit" className="btn-primary"><Plus size={14} /> Create</button>
            </form>
            <div className="space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="card flex justify-between gap-3">
                  <div>
                    <div className="font-serif text-lg">{p.title_el}</div>
                    <div className="text-xs text-dim">/{p.slug}</div>
                  </div>
                  <button onClick={() => del("blog", p.id, loadAll)} className="text-primary"><Trash2 size={18} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "schedule" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <form onSubmit={createSch} className="card space-y-3">
              <h3 className="font-serif text-xl">New Slot</h3>
              <input required className="field" placeholder="Day EL (π.χ. Δευτέρα)" value={schForm.day_el} onChange={(e) => setSchForm({ ...schForm, day_el: e.target.value })} />
              <input required className="field" placeholder="Day EN (e.g. Monday)" value={schForm.day_en} onChange={(e) => setSchForm({ ...schForm, day_en: e.target.value })} />
              <input required className="field" placeholder="Time (e.g. 18:00 - 20:00)" value={schForm.time} onChange={(e) => setSchForm({ ...schForm, time: e.target.value })} />
              <input required className="field" placeholder="Title EL" value={schForm.title_el} onChange={(e) => setSchForm({ ...schForm, title_el: e.target.value })} />
              <input required className="field" placeholder="Title EN" value={schForm.title_en} onChange={(e) => setSchForm({ ...schForm, title_en: e.target.value })} />
              <input type="number" className="field" placeholder="Order" value={schForm.order} onChange={(e) => setSchForm({ ...schForm, order: parseInt(e.target.value || "0") })} />
              <button type="submit" className="btn-primary"><Plus size={14} /> Add</button>
            </form>
            <div className="space-y-3">
              {schedule.map((s) => (
                <div key={s.id} className="card flex justify-between">
                  <div>
                    <div className="font-serif text-lg">{s.day_el} · {s.time}</div>
                    <div className="text-sm text-dim">{s.title_el}</div>
                  </div>
                  <button onClick={() => del("schedule", s.id, loadAll)} className="text-primary"><Trash2 size={18} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <form onSubmit={createGal} className="card space-y-3">
              <h3 className="font-serif text-xl">Add Media</h3>
              <select className="field" value={galForm.type} onChange={(e) => setGalForm({ ...galForm, type: e.target.value })}>
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
              <input required className="field" placeholder="URL" value={galForm.url} onChange={(e) => setGalForm({ ...galForm, url: e.target.value })} />
              <input className="field" placeholder="Caption EL" value={galForm.caption_el} onChange={(e) => setGalForm({ ...galForm, caption_el: e.target.value })} />
              <input className="field" placeholder="Caption EN" value={galForm.caption_en} onChange={(e) => setGalForm({ ...galForm, caption_en: e.target.value })} />
              <input type="number" className="field" placeholder="Order" value={galForm.order} onChange={(e) => setGalForm({ ...galForm, order: parseInt(e.target.value || "0") })} />
              <button type="submit" className="btn-primary"><Plus size={14} /> Add</button>
            </form>
            <div className="grid grid-cols-2 gap-3">
              {gallery.map((g) => (
                <div key={g.id} className="relative border border-goldSoft">
                  {g.type === "video"
                    ? <video src={g.url} className="w-full h-40 object-cover" />
                    : <img src={g.url} alt="" className="w-full h-40 object-cover" />}
                  <button onClick={() => del("gallery", g.id, loadAll)} className="absolute top-2 right-2 bg-black/70 p-1 text-primary"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <form onSubmit={createRev} className="card space-y-3">
              <h3 className="font-serif text-xl">New Review</h3>
              <input required className="field" placeholder="Student name" value={revForm.name} onChange={(e) => setRevForm({ ...revForm, name: e.target.value })} />
              <select className="field" value={revForm.rating} onChange={(e) => setRevForm({ ...revForm, rating: parseInt(e.target.value) })}>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
              </select>
              <input className="field" placeholder="Class type (e.g. Ιδιαίτερο Wing Chun)" value={revForm.class_type} onChange={(e) => setRevForm({ ...revForm, class_type: e.target.value })} />
              <textarea required className="field" rows={4} placeholder="Review text EL" value={revForm.text_el} onChange={(e) => setRevForm({ ...revForm, text_el: e.target.value })} />
              <textarea required className="field" rows={4} placeholder="Review text EN" value={revForm.text_en} onChange={(e) => setRevForm({ ...revForm, text_en: e.target.value })} />
              <input type="number" className="field" placeholder="Order" value={revForm.order} onChange={(e) => setRevForm({ ...revForm, order: parseInt(e.target.value || "0") })} />
              <button type="submit" className="btn-primary"><Plus size={14} /> Add Review</button>
            </form>
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.id} className="card flex justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-serif text-lg text-gold">{r.name}</span>
                      <span className="text-gold text-sm">{"★".repeat(r.rating || 5)}</span>
                    </div>
                    <div className="text-xs text-dim">{r.class_type}</div>
                    <p className="text-sm text-dim mt-2 italic line-clamp-3">&ldquo;{r.text_el}&rdquo;</p>
                  </div>
                  <button onClick={() => del("reviews", r.id, loadAll)} className="text-primary"><Trash2 size={18} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
